import { Button, Checkbox, FormControlLabel, useTheme } from '@mui/material';
import { MdRestartAlt } from '@teamworksdev/icons';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRange } from '../dates/RangeCalendar';
import { DateRangeFilter } from '../filters/date-range-filter';
import { SelectFilter } from '../filters/select-filter';
import type { DataTableFilter, FilterOption } from './data-table-context';

type DataTableFiltersProps<T> = {
  filters: DataTableFilter[] | undefined;
  filterState: Record<string, FilterOption[] | FilterOption>;
  setFilterState: Dispatch<
    SetStateAction<Record<string, FilterOption[] | FilterOption>>
  >;
  setSearchValue: Dispatch<React.SetStateAction<string>>;
  onResetFilters?: () => void;
};

export const DataTableFilters = <T,>({
  filters,
  filterState,
  setFilterState,
  setSearchValue,
  onResetFilters,
}: DataTableFiltersProps<T>) => {
  const { t } = useTranslation();
  const {
    palette: { info },
  } = useTheme();

  return (
    <>
      {filters?.map((f) => {
        if (f.type === 'select') {
          const selectedIds =
            (filterState[f.id] as FilterOption[])?.map((option) => option.id) ??
            [];

          const {
            title,
            isDisabled,
            multiselect,
            options,
            showSearch,
            showSelectAll,
            onChange,
          } = f;
          return (
            <SelectFilter
              key={title}
              selectedOptions={selectedIds ?? []}
              disabled={isDisabled ?? false}
              multiselect={multiselect}
              options={options.map((option: FilterOption<T>) => option.id)}
              placeholder={title}
              disableSearch={!showSearch}
              showSelectAll={showSelectAll}
              optionToName={(option) =>
                options.find((opt: FilterOption<T>) => opt.id === option)?.label
              }
              onChange={(selectedOptions) => {
                const selections = selectedOptions.map(
                  (option) =>
                    options.find(
                      (opt: FilterOption<T>) => opt.id === option,
                    ) as FilterOption,
                );
                onChange?.(selections);
                setFilterState((prev) => ({
                  ...prev,
                  [f.id]: selections,
                }));
              }}
            />
          );
        }

        if (f.type === 'dateRange') {
          return (
            <DateRangeFilter
              key={f.id}
              value={
                (filterState[f.id] as FilterOption<DateRange>)?.value ?? null
              }
              onChange={(selections) => {
                setFilterState((prev) => {
                  if (!selections) {
                    const { [f.id]: _removed, ...rest } = prev;
                    return rest;
                  }
                  return {
                    ...prev,
                    [f.id]: {
                      id: 'dateRange',
                      value: selections,
                    },
                  };
                });
              }}
            />
          );
        }

        if (f.type === 'checkbox') {
          const checked = !!(filterState[f.id] as FilterOption)?.value;
          return (
            <FormControlLabel
              key={f.id}
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    f.onChange?.(isChecked);
                    setFilterState((prev) => ({
                      ...prev,
                      [f.id]: {
                        id: f.id,
                        value: isChecked,
                      },
                    }));
                  }}
                  size="small"
                />
              }
              label={f.label}
            />
          );
        }

        throw new Error('Implement Filter component');
      })}
      <Button
        variant="text"
        className="px-2"
        startIcon={<MdRestartAlt color={info.main} size={20} />}
        onClick={() => {
          setSearchValue('');
          onResetFilters?.();
        }}
        sx={{
          color: info.main,
          whiteSpace: 'nowrap',
          minWidth: 'fit-content',
        }}
      >
        {t('resetFilters')}
      </Button>
    </>
  );
};
